import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useNotificationCounts } from "@/hooks/useNotificationCounts";
import { ArrowLeft, Loader2, Utensils, BookOpen, ShoppingBag, Megaphone, Baby, Sun, Moon, Heart, FileText, Video, ImageIcon } from "lucide-react";
import ParentMenuViewer from "@/components/parent-portal/ParentMenuViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  menu:          { label: "Menu",          icon: Utensils,    gradient: "from-green-400 to-emerald-500",   bg: "bg-green-50",   border: "border-green-200",  text: "text-green-700",  emoji: "🍽️" },
  class_project: { label: "Class Project", icon: BookOpen,    gradient: "from-purple-400 to-violet-500",   bg: "bg-purple-50",  border: "border-purple-200", text: "text-purple-700", emoji: "🎨" },
  project_needs: { label: "Items Needed",  icon: ShoppingBag, gradient: "from-orange-400 to-amber-500",    bg: "bg-orange-50",  border: "border-orange-200", text: "text-orange-700", emoji: "🛍️" },
  announcement:  { label: "Announcement",  icon: Megaphone,   gradient: "from-blue-400 to-cyan-500",       bg: "bg-blue-50",    border: "border-blue-200",   text: "text-blue-700",   emoji: "📣" },
  daily_report:  { label: "Daily Report",  icon: Baby,        gradient: "from-rose-400 to-pink-500",       bg: "bg-rose-50",    border: "border-rose-200",   text: "text-rose-700",   emoji: "👶" },
};

const MOOD_EMOJI = { happy: "😊", sad: "😢", fussy: "😤", calm: "😌", excited: "🤩", tired: "😴", good: "😊" };

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const diff = Math.floor((today - d) / 86400000);
  if (diff === 0) return "Today · " + d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
};

const DailyReportCard = ({ item }) => {
  const extra = item.extra || {};
  // mood can be a string OR an object {happy: true, fussy: false, ...}
  let moodDisplay = "";
  let moodKey = "";
  if (typeof extra.mood === "string") {
    moodDisplay = extra.mood;
    moodKey = extra.mood.toLowerCase();
  } else if (typeof extra.mood === "object" && extra.mood) {
    const active = Object.entries(extra.mood).filter(([, v]) => v).map(([k]) => k);
    moodDisplay = active.join(", ");
    moodKey = active[0] || "";
  }
  const moodEmoji = MOOD_EMOJI[moodKey] || "😊";

  return (
    <div className="rounded-3xl overflow-hidden shadow-lg border border-rose-100" data-testid={`parent-activity-item-${item.id}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-400 to-pink-500 px-5 py-4 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/20 text-2xl">👶</div>
        <div className="flex-1">
          <p className="text-white font-bold text-base">{item.subject}</p>
          <p className="text-rose-100 text-xs">{item.postedBy} · {formatDate(item.createdAt)}</p>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white px-5 py-4 space-y-4">
        {moodDisplay && (
          <div className="flex items-center gap-3 rounded-2xl bg-rose-50 px-4 py-3">
            <span className="text-2xl">{moodEmoji}</span>
            <div>
              <p className="text-xs font-semibold text-rose-600 uppercase tracking-wide">Today's Mood</p>
              <p className="text-base font-bold text-slate-800 capitalize">{moodDisplay}</p>
            </div>
          </div>
        )}

        {extra.feedings?.length > 0 && (
          <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🍼</span>
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">Feedings ({extra.feedings.length})</p>
            </div>
            <div className="space-y-1">
              {extra.feedings.map((f, i) => (
                <p key={i} className="text-sm text-slate-700">{f.time} — <span className="font-medium">{f.amount || f.type || "Fed"}</span> {f.notes ? `· ${f.notes}` : ""}</p>
              ))}
            </div>
          </div>
        )}

        {extra.diaperChanges?.length > 0 && (
          <div className="rounded-2xl bg-sky-50 border border-sky-100 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🧷</span>
              <p className="text-xs font-bold text-sky-700 uppercase tracking-wide">Diaper Changes ({extra.diaperChanges.length})</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {extra.diaperChanges.map((d, i) => (
                <span key={i} className="rounded-full bg-sky-100 text-sky-700 text-xs px-3 py-1 font-medium">{d.time} · {d.type || "Changed"}</span>
              ))}
            </div>
          </div>
        )}

        {extra.sleepPeriods?.length > 0 && (
          <div className="rounded-2xl bg-indigo-50 border border-indigo-100 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <Moon className="h-4 w-4 text-indigo-500" />
              <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide">Sleep</p>
            </div>
            <div className="space-y-1">
              {extra.sleepPeriods.map((s, i) => (
                <p key={i} className="text-sm text-slate-700">{s.startTime} – {s.endTime}</p>
              ))}
            </div>
          </div>
        )}

        {item.message && !extra.feedings?.length && (
          <p className="text-sm text-slate-600 leading-relaxed">{item.message}</p>
        )}
      </div>
    </div>
  );
};

const ActivityCard = ({ item }) => {
  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.announcement;
  const Icon = cfg.icon;

  return (
    <div className="rounded-3xl overflow-hidden shadow-lg border" style={{ borderColor: "transparent" }} data-testid={`parent-activity-item-${item.id}`}>
      <div className={`bg-gradient-to-r ${cfg.gradient} px-5 py-4 flex items-center gap-3`}>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/20 text-2xl">{cfg.emoji}</div>
        <div className="flex-1">
          <p className="text-white font-bold text-base">{item.subject}</p>
          <p className="text-white/70 text-xs">{item.postedBy} · {formatDate(item.createdAt)}</p>
        </div>
      </div>
      <div className={`${cfg.bg} px-5 py-4`}>
        {item.targetChildNames?.length > 0 && (
          <div className="mb-2 inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 px-2.5 py-0.5 text-[11px] font-semibold" data-testid={`parent-activity-targeted-${item.id}`} title="Posted privately for your family — not shared with other parents">
            🔒 Just for {item.targetChildNames.join(' & ')}
          </div>
        )}
        {item.message && (
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{item.message}</p>
        )}
        {item.attachments?.length > 0 && (
          <div className="mt-3 space-y-2">
            {/* Menu items: render with full ParentMenuViewer (PDF/Excel/Image) instead of generic attachment */}
            {item.type === "menu" && (() => {
              const att = item.attachments[0];
              const url = typeof att === "string" ? att : att?.url;
              const name = typeof att === "object" ? att?.name : null;
              const ex = item.extra || {};
              if (!url) return null;
              return (
                <ParentMenuViewer
                  fileType={ex.fileType}
                  fileUrl={url}
                  filename={name}
                  month={ex.month}
                  menuType={ex.menuType}
                  hasPdf={ex.hasPdf}
                />
              );
            })()}
            {item.type !== "menu" && item.attachments.map((att, i) => {
              // Normalize: att may be a string (legacy) or {url, contentType, name} object
              const url = typeof att === 'string' ? att : att?.url;
              const ct = typeof att === 'object' ? att?.contentType : null;
              const name = typeof att === 'object' ? att?.name : null;

              // Detect type from contentType or extension
              const urlLower = (url || '').toLowerCase().split('?')[0];
              const isVideo = ct === 'video' || ['.mp4', '.mov', '.avi', '.webm'].some(e => urlLower.endsWith(e));
              const isPdf   = ct === 'pdf'   || urlLower.endsWith('.pdf');
              const isImage = !isVideo && !isPdf;

              if (!url) return null;

              return (
                <div key={i} className="rounded-2xl overflow-hidden border border-white/50 shadow-sm bg-white/60" data-testid={`activity-attachment-${item.id}-${i}`}>
                  {isImage && (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <img
                        src={url}
                        alt={name || `Photo ${i + 1}`}
                        className="w-full max-h-72 object-cover rounded-2xl"
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                      />
                    </a>
                  )}
                  {isVideo && (
                    <video
                      src={url}
                      controls
                      className="w-full max-h-72 rounded-2xl"
                      preload="metadata"
                    />
                  )}
                  {isPdf && (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold ${cfg.text} hover:underline`}
                    >
                      <FileText className="w-4 h-4 flex-shrink-0" />
                      {name || `View Document ${i + 1}`}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const FILTER_TABS = [
  { key: "all",          label: "All",          emoji: "✨" },
  { key: "daily_report", label: "Daily Report",  emoji: "👶" },
  { key: "menu",         label: "Menu",          emoji: "🍽️" },
  { key: "announcement", label: "News",          emoji: "📣" },
  { key: "class_project",label: "Projects",      emoji: "🎨" },
  { key: "project_needs",label: "Items Needed",  emoji: "🛍️" },
];

export default function ParentActivityPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { markSeen } = useNotificationCounts();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Read pre-selected filter from URL e.g. ?filter=announcement
  const urlFilter = new URLSearchParams(location.search).get("filter") || "all";
  const [filter, setFilter] = useState(urlFilter);

  useEffect(() => {
    markSeen("activity");
    axios.get(`${API}/parent-communication/parent/activity`)
      .then((res) => setItems(res.data.items || []))
      .catch(() => toast.error("Unable to load activity feed"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.type === filter);

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-emerald-50 px-4 py-6" data-testid="parent-activity-page">
      <div className="mx-auto max-w-lg space-y-5">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/parent")} className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200" data-testid="parent-activity-back-btn">
            <ArrowLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Daily Activity</h1>
            <p className="text-sm text-slate-500">Updates from your center</p>
          </div>
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {FILTER_TABS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex-shrink-0 flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition whitespace-nowrap ${
                filter === f.key
                  ? "bg-slate-900 text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200 hover:border-slate-300"
              }`}
              data-testid={`parent-activity-filter-${f.key}`}
            >
              <span>{f.emoji}</span> {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
            <p className="text-sm text-slate-400">Loading your updates...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="parent-activity-empty">
            <div className="text-5xl mb-4">🌟</div>
            <p className="font-semibold text-slate-700">No updates yet</p>
            <p className="text-sm text-slate-400 mt-1">Check back soon — your center will post updates here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((item) =>
              item.type === "daily_report"
                ? <DailyReportCard key={item.id} item={item} />
                : <ActivityCard key={item.id} item={item} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
